import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, ChevronDown, Search, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { getRegistrationSessionByToken } from '../../lib/services/registrationSession';
import { NotionTypingInput } from '../../components/ui/notion/NotionTypingInput';
import { useExitWarning } from '../../lib/hooks';
import { useRegistrationContext } from '../../lib/hooks/useRegistrationContext';
import { RegistrationReferralBanner } from '../../components/registration/RegistrationReferralBanner';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';

interface CompanyFormData {
  name: string;
  businessSectorId: string;
  companySize: string;
  partnerCode: string;
}

// Extract user-friendly error message + optional field from a supabase-js invoke error.
// supabase.functions.invoke() returns a FunctionsHttpError with a generic
// "Edge Function returned a non-2xx status code" message on any non-2xx.
// The real reason (and field, for field-level errors) is in the response body.
async function extractInvokeError(invokeError: any): Promise<{ message: string; field?: string }> {
  if (invokeError?.context && typeof invokeError.context.json === 'function') {
    try {
      const body = await invokeError.context.json();
      if (body?.error && typeof body.error === 'string') {
        return { message: body.error, field: typeof body.field === 'string' ? body.field : undefined };
      }
    } catch {
      // Body not JSON or already consumed — fall through.
    }
  }
  return { message: invokeError?.message || 'Errore durante il salvataggio' };
}

const TEAM_SIZES = [
  { id: 'solo',  label: 'Solo io', sub: '1 persona' },
  { id: 'small', label: '2 – 5',   sub: 'Piccolo team' },
  { id: 'med',   label: '6 – 20',  sub: 'PMI' },
  { id: 'big',   label: '21+',     sub: 'Strutturata' },
] as const;

interface CompanySector {
  id: string;
  name: string;
  description: string;
}

export function CompanyDetailsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('token');

  const { referrerName, referrerType, trialDays } = useRegistrationContext({ sessionToken });

  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    businessSectorId: '',
    companySize: '',
    partnerCode: ''
  });

  const [companySectors, setCompanySectors] = useState<CompanySector[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSectors, setIsLoadingSectors] = useState(true);
  const [errors, setErrors] = useState<Partial<CompanyFormData>>({});
  const [showPartnerField, setShowPartnerField] = useState(false);

  // Searchable dropdown state
  const [sectorSearchQuery, setSectorSearchQuery] = useState('');
  const [isSectorDropdownOpen, setIsSectorDropdownOpen] = useState(false);
  const sectorDropdownRef = useRef<HTMLDivElement>(null);

  // Warning on page exit during registration
  useExitWarning(true, 'Sei sicuro di voler abbandonare la registrazione?');

  // Filter sectors based on search query
  const filteredSectors = companySectors.filter(sector =>
    sector.name.toLowerCase().includes(sectorSearchQuery.toLowerCase())
  );

  // Get selected sector name
  const selectedSectorName = companySectors.find(s => s.id === formData.businessSectorId)?.name || '';

  useEffect(() => { trackRegisterStepViewed('company'); }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sectorDropdownRef.current && !sectorDropdownRef.current.contains(event.target as Node)) {
        setIsSectorDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/register');
      return;
    }

    loadCompanySectors();
    loadSessionData();
  }, [sessionToken, navigate]);

  // Load existing session data to restore form values
  const loadSessionData = async () => {
    try {
      const { data, error } = await getRegistrationSessionByToken(sessionToken);

      if (error || !data) return;

      // Restore company data if it exists
      if (data.company_data) {
        const { name, businessSectorId, companySize } = data.company_data;
        setFormData(prev => ({
          ...prev,
          name: name || '',
          businessSectorId: businessSectorId || '',
          companySize: companySize || ''
        }));
      }
    } catch (error) {
      console.error('Error loading session data:', error);
    }
  };

  const loadCompanySectors = async () => {
    try {
      const { data, error } = await supabase
        .from('company_sectors')
        .select('id, name, description')
        .eq('active', true)
        .order('sort_order');

      if (error) {
        console.error('Error loading company sectors:', error);
        return;
      }

      setCompanySectors(data || []);
    } catch (error) {
      console.error('Error loading company sectors:', error);
    } finally {
      setIsLoadingSectors(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CompanyFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome azienda è obbligatorio';
    }

    if (!formData.businessSectorId.trim()) {
      newErrors.businessSectorId = 'Seleziona un settore aziendale';
    }

    if (!formData.companySize) {
      newErrors.companySize = 'Seleziona la dimensione del team';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('register-company', {
        body: {
          sessionToken,
          companyData: { name: formData.name, businessSectorId: formData.businessSectorId, companySize: formData.companySize },
          partnerCode: formData.partnerCode.trim() || null
        }
      });

      if (error) {
        // 400 con field=partnerCode → errore sul campo, niente alert
        const { message, field } = await extractInvokeError(error);
        if (field === 'partnerCode') {
          setShowPartnerField(true);
          setErrors(prev => ({ ...prev, partnerCode: message }));
          return;
        }
        throw new Error(message);
      }

      if (data.error) {
        throw new Error(data.error || 'Errore durante il salvataggio');
      }

      navigate(`/register/complete?token=${sessionToken}`);

    } catch (error) {
      console.error('Company details error:', error);
      alert(error instanceof Error ? error.message : 'Errore durante il salvataggio');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CompanyFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!sessionToken) {
    return null;
  }

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.company} step={5}>
      <RegistrationReferralBanner
        referrerName={referrerName}
        referrerType={referrerType}
        trialDays={trialDays}
      />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">
          La tua azienda
        </h1>
        <p className="text-[#787774] text-sm">
          Inserisci i dati per personalizzare il CRM
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company Name */}
        <NotionTypingInput
          label="Nome Azienda"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholders={["Mario Rossi S.r.l.", "TechStart", "Studio Bianchi"]}
          icon={<Building2 className="w-4 h-4" />}
          error={errors.name}
          required
        />

        {/* Business Sector - Searchable Dropdown */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-[#37352F]">
            Settore Aziendale
          </label>
          {isLoadingSectors ? (
            <div className="flex items-center gap-2 py-2.5 px-3 border border-[rgba(55,53,47,0.16)] rounded-lg bg-[rgba(55,53,47,0.02)]">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#2383E2] border-t-transparent"></div>
              <span className="text-sm text-[#787774]">Caricamento settori...</span>
            </div>
          ) : (
            <div ref={sectorDropdownRef} className="relative">
              {/* Trigger button */}
              <button
                type="button"
                onClick={() => {
                  setIsSectorDropdownOpen(!isSectorDropdownOpen);
                  setSectorSearchQuery('');
                }}
                className={`
                  w-full px-3 py-2.5 pr-10 text-left
                  notion-typing-input
                  text-sm
                  ${!formData.businessSectorId ? 'text-[#787774]' : 'text-[#37352F]'}
                  ${errors.businessSectorId ? '!border-red-500' : ''}
                  ${isSectorDropdownOpen ? '!border-[#2383E2] !shadow-[0_0_0_3px_rgba(35,131,226,0.15)]' : ''}
                `}
              >
                {selectedSectorName || 'Seleziona il settore della tua azienda'}
              </button>
              <ChevronDown
                className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#787774] pointer-events-none transition-transform duration-200 ${isSectorDropdownOpen ? 'rotate-180' : ''}`}
              />

              {/* Dropdown */}
              <AnimatePresence>
                {isSectorDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute z-50 w-full mt-1.5 bg-white rounded-lg border border-[rgba(55,53,47,0.16)] shadow-lg overflow-hidden"
                  >
                    {/* Search input */}
                    <div className="p-2 border-b border-[rgba(55,53,47,0.09)]">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#787774]" />
                        <input
                          type="text"
                          value={sectorSearchQuery}
                          onChange={(e) => setSectorSearchQuery(e.target.value)}
                          placeholder="Cerca settore..."
                          className="w-full pl-8 pr-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] border-none rounded-md focus:outline-none focus:ring-2 focus:ring-[#2383E2] focus:ring-opacity-30 placeholder:text-[#787774]"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Options list */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredSectors.length > 0 ? (
                        filteredSectors.map((sector) => (
                          <button
                            key={sector.id}
                            type="button"
                            onClick={() => {
                              handleInputChange('businessSectorId', sector.id);
                              setIsSectorDropdownOpen(false);
                              setSectorSearchQuery('');
                            }}
                            className={`
                              w-full px-3 py-2.5 text-left text-sm flex items-center justify-between
                              hover:bg-[rgba(55,53,47,0.04)] transition-colors
                              ${formData.businessSectorId === sector.id ? 'bg-[rgba(35,131,226,0.08)] text-[#2383E2]' : 'text-[#37352F]'}
                            `}
                          >
                            <span>{sector.name}</span>
                            {formData.businessSectorId === sector.id && (
                              <Check className="w-4 h-4 text-[#2383E2]" />
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-sm text-[#787774] text-center">
                          Nessun settore trovato
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          {errors.businessSectorId && (
            <p className="text-sm text-red-600">{errors.businessSectorId}</p>
          )}
        </div>

        {/* Team size picker */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-[#37352F]">
            Quanti siete?
          </label>
          <p className="text-xs text-[#787774] mb-2">
            Ci aiuta a personalizzare la dashboard.
          </p>
          <div className="grid grid-cols-4 gap-2">
            {TEAM_SIZES.map(({ id, label, sub }) => {
              const isSelected = formData.companySize === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleInputChange('companySize', id)}
                  className={`
                    relative flex flex-col items-center text-center rounded-lg px-2 py-3 text-sm border transition-all
                    ${isSelected
                      ? 'border-[#2383E2] bg-[rgba(35,131,226,0.06)] shadow-[0_0_0_3px_rgba(35,131,226,0.12)]'
                      : 'border-[rgba(55,53,47,0.16)] bg-[rgba(55,53,47,0.02)] hover:border-[rgba(55,53,47,0.3)] hover:bg-[rgba(55,53,47,0.04)]'
                    }
                  `}
                >
                  {isSelected && (
                    <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-[#2383E2] text-white inline-flex items-center justify-center">
                      <Check className="w-2 h-2" strokeWidth={3} />
                    </span>
                  )}
                  <span className={`font-semibold text-sm ${isSelected ? 'text-[#2383E2]' : 'text-[#37352F]'}`}>
                    {label}
                  </span>
                  <span className="text-[11px] text-[#787774] mt-0.5 leading-tight">
                    {sub}
                  </span>
                </button>
              );
            })}
          </div>
          {errors.companySize && (
            <p className="text-sm text-red-600">{errors.companySize}</p>
          )}
        </div>

        {/* Codice partner: link discreto che apre il campo */}
        {!showPartnerField ? (
          <button
            type="button"
            onClick={() => setShowPartnerField(true)}
            className="text-xs text-[#787774] hover:text-[#2383E2] underline underline-offset-2 transition-colors"
          >
            Hai un codice partner?
          </button>
        ) : (
          <NotionTypingInput
            label="Codice partner"
            value={formData.partnerCode}
            onChange={(e) => handleInputChange('partnerCode', e.target.value.toUpperCase())}
            placeholders={['ABCDE']}
            maxLength={5}
            hint="Codice di 5 lettere fornito dal partner (facoltativo)"
            error={errors.partnerCode}
            className="font-mono uppercase"
          />
        )}

        {/* Help text */}
        <div className="bg-[rgba(55,53,47,0.02)] rounded-lg p-3 border border-[rgba(55,53,47,0.06)]">
          <p className="text-xs text-[#787774] text-center">
            Potrai aggiungere altri dettagli aziendali dopo aver attivato il tuo account
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-[rgba(55,53,47,0.09)]">
          <button
            type="button"
            onClick={() => navigate(`/register/profile?token=${sessionToken}`)}
            className="notion-register-btn-ghost inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="notion-register-btn"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Salvataggio...</span>
              </>
            ) : (
              <>
                <span>Continua</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </RegisterSplitLayout>
  );
}
